// src/app/pages/sales-invoices/components/ItemSearchModal.tsx
import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog' // [cite: 170]
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem
} from '@/components/ui/command' // [cite: 171]
import { useQuery } from '@tanstack/react-query' // [cite: 172]
import { useDebounce } from '@/hooks/useDebounce' // [cite: 172]
import { Loader2 } from 'lucide-react' // [cite: 172]

// --- SQLite Integration ---
// Function to safely access the SQLite API without TypeScript errors
const getSQLiteApi = () => {
  // @ts-ignore - Ignore TypeScript errors for window.api access
  if (window.api && window.api.db) {
    // @ts-ignore - SQLite API is available
    return window.api.db
  }
  return null
}

// --- Configuration ---
// Set this flag to control search behavior:
// true = use SQLite, false = use API or fall back to local JSON
const USE_SQLITE_SEARCH = true

// Define types for the component
export interface ItemSearchResult {
  id: number
  name: string
  code?: string | null
  description?: string | null // [cite: 174]
  stock?: number | null // [cite: 174]
  tax_rate_id?: number | null // Include tax_rate_id for calculating tax
  [key: string]: unknown // [cite: 174]
}

interface ItemSearchModalProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void // [cite: 175]
  onSelectItem: (item: ItemSearchResult) => void // [cite: 175]
  // API search function is now optional if using SQLite
  searchItems?: (query: string) => Promise<ItemSearchResult[]>
  // External loading state might still be relevant
  isLoading?: boolean // [cite: 175]
}

export const OfflineItemSearchModal: React.FC<ItemSearchModalProps> = ({
  isOpen,
  onOpenChange,
  onSelectItem,
  searchItems, // API search function
  isLoading: externalLoading = false
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 200) // Increased debounce for SQLite
  const inputRef = useRef<HTMLInputElement>(null) // [cite: 177]
  const [selectedItemIndex, setSelectedItemIndex] = useState(0) // [cite: 177]
  const [sqliteSearchResults, setSqliteSearchResults] = useState<ItemSearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState<Error | null>(null)

  // --- SQLite Search Logic ---
  useEffect(() => {
    // Only search when we have a query and modal is open
    if (USE_SQLITE_SEARCH && isOpen && debouncedSearchQuery.length > 1) {
      const performSQLiteSearch = async () => {
        setIsSearching(true)
        setSearchError(null)

        try {
          console.log('[ItemSearchModal] Searching SQLite for:', debouncedSearchQuery)
          const sqliteApi = getSQLiteApi()

          if (sqliteApi && sqliteApi.searchItems) {
            const results = await sqliteApi.searchItems(debouncedSearchQuery)
            console.log('[ItemSearchModal] SQLite search results:', results.length, 'items')
            setSqliteSearchResults(results)
          } else {
            console.warn('[ItemSearchModal] SQLite API not available')
            setSqliteSearchResults([])
            setSearchError(new Error('SQLite search unavailable'))
          }
        } catch (error) {
          console.error('[ItemSearchModal] SQLite search error:', error)
          setSqliteSearchResults([])
          setSearchError(error instanceof Error ? error : new Error('Unknown search error'))
        } finally {
          setIsSearching(false)
        }
      }

      performSQLiteSearch()
    } else if (USE_SQLITE_SEARCH) {
      // Clear results if query is too short
      setSqliteSearchResults([])
    }
  }, [debouncedSearchQuery, isOpen])

  // --- API Search Query ---
  const {
    data: apiSearchResults,
    isLoading: isApiLoading,
    isError: isApiError,
    error: apiError
  } = useQuery({
    queryKey: ['itemSearch', debouncedSearchQuery],
    queryFn: () => {
      if (!searchItems) {
        console.warn('API search function (`searchItems`) is not provided.')
        return Promise.resolve([])
      }
      return searchItems(debouncedSearchQuery)
    },
    // Enable API query only if not using SQLite
    enabled: isOpen && !USE_SQLITE_SEARCH && debouncedSearchQuery.length > 1 && !!searchItems,
    staleTime: 1000 * 60 * 5 // Cache results for 5 minutes
  })

  // --- Determine Final Results and Loading State ---
  const searchResults = USE_SQLITE_SEARCH ? sqliteSearchResults : apiSearchResults
  const isLoading = USE_SQLITE_SEARCH ? isSearching : externalLoading || isApiLoading
  const isError = USE_SQLITE_SEARCH ? !!searchError : isApiError
  const error = USE_SQLITE_SEARCH ? searchError : apiError

  // Focus input and reset selection on modal open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus()
        inputRef.current?.select()
      }, 50)
      setSelectedItemIndex(0)
    } else {
      // Clear search query when modal closes
      setSearchQuery('')
      if (USE_SQLITE_SEARCH) setSqliteSearchResults([]) // Clear results too
    }
  }, [isOpen]) // [cite: 179] modification

  // Reset selection when results change
  useEffect(() => {
    setSelectedItemIndex(0)
  }, [searchResults]) // [cite: 180]

  const handleSelect = (item: ItemSearchResult): void => {
    onSelectItem(item)
    onOpenChange(false) // [cite: 182]
    // No need to clear query here, handled by isOpen effect
  } //

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.nativeEvent.isComposing) return // [cite: 183]

    if (searchResults && searchResults.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setSelectedItemIndex((prev) => (prev < searchResults.length - 1 ? prev + 1 : prev)) // [cite: 185]
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        setSelectedItemIndex((prev) => (prev > 0 ? prev - 1 : 0)) // [cite: 187]
      } else if (event.key === 'Enter') {
        event.preventDefault()
        if (selectedItemIndex >= 0 && selectedItemIndex < searchResults.length) {
          handleSelect(searchResults[selectedItemIndex]) // [cite: 189]
        }
      }
    }

    if (event.key === 'Escape') {
      onOpenChange(false) // [cite: 190]
    }
  } //

  // Determine search type label for the dialog title
  const searchTypeLabel = USE_SQLITE_SEARCH ? 'SQLite' : 'API'

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[600px] p-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onKeyDown={handleKeyDown} // [cite: 191]
      >
        <Command shouldFilter={false}>
          {' '}
          {/* Disable Command's built-in filtering */}
          <DialogHeader className="p-4 pb-0">
            <DialogTitle>Search Products / Services ({searchTypeLabel})</DialogTitle>{' '}
            {/* [cite: 192] modification */}
          </DialogHeader>
          <CommandInput
            ref={inputRef}
            placeholder="Type item name, code, or description..."
            value={searchQuery}
            onValueChange={setSearchQuery}
            className="h-11 mx-4 my-2" // [cite: 192]
          />
          <CommandList className="max-h-[400px] p-4 pt-0 overflow-y-auto">
            {' '}
            {/* [cite: 193] */}
            {isLoading && (
              <div className="p-4 text-center">
                <Loader2 className="h-6 w-6 animate-spin inline-block" />
              </div> // [cite: 193]
            )}
            {!isLoading &&
              debouncedSearchQuery.length > 1 &&
              (!searchResults || searchResults.length === 0) && (
                <CommandEmpty>No products found.</CommandEmpty> // [cite: 195]
              )}
            {isError && (
              <div className="p-4 text-destructive">
                Error: {(error as Error)?.message || 'Could not fetch items'}
              </div>
            )}{' '}
            {/* [cite: 196] */}
            <CommandGroup>
              {searchResults?.map((item, index) => (
                <CommandItem
                  key={item.id}
                  // Use a unique value for cmdk internal keying if names/codes aren't unique
                  value={`item-${item.id}-${item.name}`} // Changed value to be more unique
                  onSelect={() => handleSelect(item)}
                  className={`flex justify-between items-center cursor-pointer p-2 rounded-md ${index === selectedItemIndex ? 'bg-accent' : ''}`} // [cite: 197] modified className
                  // Scroll into view logic (optional but recommended for keyboard navigation)
                  ref={(el) => {
                    if (el && index === selectedItemIndex) {
                      el.scrollIntoView({ block: 'nearest' })
                    }
                  }}
                >
                  <div>
                    <span className="font-medium">{item.name}</span> {/* [cite: 198] */}
                    {item.code && (
                      <span className="text-sm text-muted-foreground ml-2">({item.code})</span>
                    )}{' '}
                    {/* [cite: 198] */}
                    {item.description && (
                      <div className="text-xs text-muted-foreground mt-1">{item.description}</div>
                    )}{' '}
                    {/* [cite: 198] */}
                  </div>
                  {item.stock !== undefined && (
                    <span className="text-sm">{item.stock} in stock</span>
                  )}{' '}
                  {/* [cite: 199] */}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  ) // [cite: 200]
}
